// RITA-1534 (Epic RITA-901) — Auswahlfeld „Schriftgröße“ der Werkzeugleiste (Entscheidung Julia 07.10.2026):
// EIN Feld direkt rechts neben Unterstrichen mit eigenem Listenmenü (kein natives Auswahlfeld). Es zeigt
// die wirksame Größe an der Schreibmarke bzw. der Markierung, bei gemischt großer Markierung „–“. Die
// Grundgröße des Bereichs ist in der Liste gekennzeichnet („(Grundgröße)“, in der Fußzeile „(Standard
// Fußzeile)“); sie zu wählen entfernt die eigene Größe, der Text folgt dann wieder der Grundgröße. Der
// letzte Eintrag öffnet das kleine Fenster „Grundgröße für den ganzen Vertrag“.
//
// Fokus: Menü und Fenster nehmen den Fokus, anders als die Knöpfe daneben. Deshalb holt das Öffnen die
// DOM-Auswahl ins Modell (`auswahlLeser`), und die Befehle arbeiten auf der Modellauswahl. Das Menü läuft
// ohne MUI-Fokusfalle (`disableEnforceFocus`) und legt den Fokus SOFORT um, noch während es ausblendet: Die
// Wahl einer Größe holt die Schreibmarke zurück (Befehl mit Fokusanforderung), ein Abbruch gibt den Fokus dem
// Knopf. Mit Fokusfalle holte MUI jeden Fokuswechsel ins Menü zurück, und die durch den Seitenneuaufbau
// kollabierte Browserauswahl wäre ins Modell übernommen worden; ein erst nach dem Ausblenden umgelegter
// Fokus überholte dagegen den nächsten Klick oder Tastendruck (beides in Chromium gemessen, 09.10.2026).
// Tab schließt das Menü ohnehin. Das Fenster behält die Fokusfalle (modal) und gibt den Fokus erst nach dem
// Ausblenden zurück (`TransitionProps.onExited`: Übernehmen → Schreibmarke, Abbrechen → Knopf).
// `disableRestoreFocus` bei beiden, weil MUI den Fokus sonst nach dem Ausblenden selbst umlegte.
import React, { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import { VertragEditorApi } from '../editor/useVertragEditor';
import { GRUNDGROESSEN_PT, grundgroesseVon, SCHRIFTGROESSEN_PT } from '../model/schriftgroessen';

export const SCHRIFTGROESSE_FELDNAME = 'Schriftgröße';
export const GRUNDGROESSE_AENDERN = 'Grundgröße ändern …';
export const GRUNDGROESSE_TITEL = 'Grundgröße für den ganzen Vertrag';
export const GRUNDGROESSE_HINWEIS =
  'Gilt für allen Text ohne eigene Größe, auch in der Kopfzeile. Die Fußzeile bleibt bei 9 pt.';
export const GRUNDGROESSE_FELDNAME = 'Grundgröße';

interface Props {
  editor: VertragEditorApi;
  gesperrt?: boolean;
}

/** Wohin der Fokus nach dem Ausblenden des Fensters „Grundgröße“ geht. */
type Fokusziel = 'editor' | 'knopf';

const fokusHalten = (event: React.MouseEvent) => event.preventDefault();

/** Feste Liste; gespeicherte Größen außerhalb der Liste (nur per Schnittstelle möglich) werden einsortiert. */
function mitWerten(liste: readonly number[], werte: Array<number | null>): number[] {
  const fehlend = werte.filter((wert): wert is number => wert !== null && !liste.includes(wert));
  if (fehlend.length === 0) return [...liste];
  return Array.from(new Set([...liste, ...fehlend])).sort((a, b) => a - b);
}

function Schriftgroessenwahl({ editor, gesperrt = false }: Props) {
  const knopfRef = useRef<HTMLButtonElement>(null);
  const fokuszielRef = useRef<Fokusziel>('knopf');
  const [menueAnker, setMenueAnker] = useState<HTMLElement | null>(null);
  /** Wert im Fenster „Grundgröße“; null = Fenster geschlossen. */
  const [fensterWert, setFensterWert] = useState<number | null>(null);
  const { bereich, dokument } = editor.zustand;
  const grundgroesse = grundgroesseVon(dokument, bereich);
  const aktuell = editor.groesseAktuell;
  const kennzeichnung = bereich === 'fuss' ? '(Standard Fußzeile)' : '(Grundgröße)';
  const offen = menueAnker !== null;

  // Läuft nach dem vollständigen Ausblenden des Fensters (Fokusfalle beendet). Ohne Modellauswahl (noch kein
  // Klick in den Vertrag) kann der Editor die Schreibmarke nicht setzen – dann bleibt der Fokus beim Knopf.
  const fensterAusgeblendet = () => {
    if (fokuszielRef.current === 'editor' && editor.zustand.auswahl) editor.befehle.fokusAnfordern();
    else knopfRef.current?.focus();
  };
  const oeffnen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (gesperrt) return;
    // Vor dem Öffnen die DOM-Auswahl ins Modell übernehmen (selectionchange kommt asynchron); das Menü
    // zeigt dann die Größe dieser Auswahl, und der Befehl arbeitet später auf ihr.
    editor.auswahlLeser.current?.();
    setMenueAnker(event.currentTarget);
  };
  const abbrechen = () => {
    setMenueAnker(null);
    knopfRef.current?.focus();
  };
  const waehlen = (groessePt: number) => {
    setMenueAnker(null);
    // Der Befehl holt die Schreibmarke sofort zurück; ohne Modellauswahl ändert er nichts, dann bleibt der
    // Fokus beim Knopf.
    editor.befehle.groesse(groessePt);
    if (!editor.zustand.auswahl) knopfRef.current?.focus();
  };
  const fensterOeffnen = () => {
    setMenueAnker(null);
    setFensterWert(dokument.schrift.groessePt);
  };
  const fensterAbbrechen = () => {
    fokuszielRef.current = 'knopf';
    setFensterWert(null);
  };
  const uebernehmen = () => {
    fokuszielRef.current = 'editor';
    if (fensterWert !== null) editor.befehle.grundgroesse(fensterWert);
    setFensterWert(null);
  };

  return (
    <>
      <Tooltip title={SCHRIFTGROESSE_FELDNAME}>
        <span>
          <Button
            ref={knopfRef}
            id="vertrag-schriftgroesse"
            size="small"
            aria-label={SCHRIFTGROESSE_FELDNAME}
            aria-haspopup="menu"
            aria-expanded={offen ? true : undefined}
            data-testid="vertrag-schriftgroesse"
            disabled={gesperrt}
            onMouseDown={fokusHalten}
            onClick={oeffnen}
            endIcon={<ArrowDropDownIcon />}
            sx={{ minWidth: 76, px: 1, textTransform: 'none', color: 'text.primary', fontWeight: 400 }}
          >
            {aktuell === null ? '–' : `${aktuell} pt`}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={menueAnker}
        open={offen}
        onClose={abbrechen}
        disableRestoreFocus
        disableEnforceFocus
        MenuListProps={{ dense: true, 'aria-labelledby': 'vertrag-schriftgroesse' }}
      >
        {mitWerten(SCHRIFTGROESSEN_PT, [aktuell, grundgroesse]).map((groesse) => {
          const gewaehlt = groesse === aktuell;
          const bezeichnung = groesse === grundgroesse ? `${groesse} pt ${kennzeichnung}` : `${groesse} pt`;
          return (
            <MenuItem
              key={groesse}
              role="menuitemradio"
              aria-checked={gewaehlt}
              selected={gewaehlt}
              data-groesse={groesse}
              onClick={() => waehlen(groesse)}
            >
              <ListItemIcon>{gewaehlt && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText>{bezeichnung}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem onClick={fensterOeffnen} data-testid="vertrag-grundgroesse-aendern">
          <ListItemIcon />
          <ListItemText>{`${GRUNDGROESSE_AENDERN} (jetzt ${dokument.schrift.groessePt} pt)`}</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={fensterWert !== null}
        onClose={fensterAbbrechen}
        disableRestoreFocus
        TransitionProps={{ onExited: fensterAusgeblendet }}
        aria-labelledby="vertrag-grundgroesse-titel"
      >
        <DialogTitle id="vertrag-grundgroesse-titel">{GRUNDGROESSE_TITEL}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{GRUNDGROESSE_HINWEIS}</DialogContentText>
          <TextField
            select
            size="small"
            label={GRUNDGROESSE_FELDNAME}
            value={String(fensterWert ?? dokument.schrift.groessePt)}
            onChange={(event) => setFensterWert(Number(event.target.value))}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': GRUNDGROESSE_FELDNAME }}
            sx={{ minWidth: 160 }}
          >
            {mitWerten(GRUNDGROESSEN_PT, [fensterWert ?? dokument.schrift.groessePt]).map((groesse) => (
              <option key={groesse} value={String(groesse)}>
                {`${groesse} pt`}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={fensterAbbrechen}>Abbrechen</Button>
          <Button variant="contained" onClick={uebernehmen}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Schriftgroessenwahl;
